<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $t('messages.dashboard'),
                        href: meta.links.dashboard_index,
                    },
                    {
                        name: $t('messages.staff'),
                        href: meta.links.index,
                    },
                    {
                        name: full_name,
                        href: null,
                    },
                    {
                        name: $t('messages.edit'),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.update" method="post" @submit.prevent="submit">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.name') }}
                    <div class="text-gray-400">
                        {{ full_name }}
                    </div>
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.first_name')" for="first_name" />
                            <FormInput
                                required
                                id="first_name"
                                v-model.trim="form.first_name"
                                name="first_name"
                                type="text"
                                max="60"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.first_name" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.nickname')" for="name" />
                            <FormInput
                                id="name"
                                v-model.trim="form.name"
                                name="name"
                                type="text"
                                max="191"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.name" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.last_name')" for="last_name" />
                            <FormInput
                                required
                                id="last_name"
                                v-model.trim="form.last_name"
                                name="last_name"
                                type="text"
                                max="60"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.last_name" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.second_last_name')" for="second_last_name" />
                            <FormInput
                                id="second_last_name"
                                v-model.trim="form.second_last_name"
                                name="second_last_name"
                                type="text"
                                max="60"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.second_last_name" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.information') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.date_of_birth')" for="date_of_birth" />
                            <FormInput
                                id="date_of_birth"
                                v-model="form.date_of_birth"
                                name="date_of_birth"
                                type="date"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.date_of_birth" class="mt-2" />
                        </div>
                    </div>

                    <div class="md:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.identification_document')" for="identification_document" />
                            <FormInput
                                id="identification_document"
                                v-model="form.identification_document"
                                name="identification_document"
                                type="text"
                                max="60"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.identification_document" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.gender')" for="gender" />
                            <FormSelect
                                required
                                id="gender"
                                v-model="form.gender"
                                :options="options.genders"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.gender" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.mother_tongue')" for="mother_tongue" />
                            <FormSelect
                                id="mother_tongue"
                                v-model="form.mother_tongue"
                                :options="options.languages"
                                optgroups
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.mother_tongue" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.nationality')" for="nationality" />
                            <FormSelect
                                id="nationality"
                                v-model="form.nationality"
                                :options="options.countries"
                                optgroups
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.nationality" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.country_of_origin')" for="country_of_origin" />
                            <FormSelect
                                id="country_of_origin"
                                v-model="form.country_of_origin"
                                :options="options.countries"
                                optgroups
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.country_of_origin" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.settings') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.preferred_language')" for="preferred_language" />
                            <FormSelect
                                id="preferred_language"
                                v-model="form.preferred_language"
                                :options="options.preferred_languages"
                                optgroups
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.preferred_language" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.contact_info') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.email')" for="email" />
                            <FormInput
                                required
                                id="email"
                                v-model="form.email"
                                name="email"
                                type="email"
                                max="191"
                                autocomplete="off"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.email" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.password') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.password')" for="password" />
                            <FormInput
                                id="password"
                                v-model="form.password"
                                name="password"
                                autocomplete="new-password"
                                type="password"
                                max="191"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.password" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.password_repeat')" for="password_confirmation" />
                            <FormInput
                                id="password_confirmation"
                                v-model="form.password_confirmation"
                                name="password_confirmation"
                                autocomplete="new-password"
                                type="password"
                                max="191"
                                class="mt-1 w-full"
                            />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <div class="flex justify-end mt-5">
                <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
            </div>
        </form>
    </PanelPage>
</template>

<script>
import AdminLayout from '@/Shared/AdminLayout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        PanelPage,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
    },
    layout: AdminLayout,
    name: 'Edit',
    props: ['user', 'meta', 'options'],
    data() {
        return {
            form: this.$inertia.form({
                first_name: this.user.first_name,
                name: this.user.name,
                last_name: this.user.last_name,
                second_last_name: this.user.second_last_name,
                date_of_birth: this.user.date_of_birth,
                email: this.user.email,
                active: this.user.active,
                gender: this.user.gender,
                identification_document: this.user.identification_document,
                mother_tongue: this.user.mother_tongue,
                nationality: this.user.nationality,
                country_of_origin: this.user.country_of_origin,
                password: '',
                password_confirmation: '',
                preferred_language: this.user.preferred_language,
            }),
        };
    },
    methods: {
        submit() {
            this.form.patch(this.meta.links.update);
        },
    },
    computed: {
        full_name: function () {
            if (!this.form.last_name && !this.form.second_last_name) {
                return '';
            }

            const lastNames = (this.form.last_name + ' ' + (this.form.second_last_name || '')).trim();
            const firstName = this.form.name ? this.form.name : this.form.first_name;
            return `${lastNames}, ${firstName}`;
        },
    },
};
</script>
